import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';

import { Icon } from '../Icon';

import type { DropzoneOptions, FileWithPath } from 'react-dropzone';

export type File = FileWithPath;

export interface FileDropzoneProps extends DropzoneOptions {
  caption?: string | React.ReactNode;
  files?: File[];
  onRemove?: (file: File) => void;
  onRemoveAll?: () => void;
  onUpload?: () => void;
  disabled?: boolean;
}

export function FileDropzone({
  caption,
  multiple = true,
  disabled = false,
  ...props
}: FileDropzoneProps): React.ReactNode {
  const { t } = useTranslation('translation', { keyPrefix: 'fileDropzone' });
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...props,
    disabled,
  });
  const inputProps = { ...getInputProps(), multiple };

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          alignItems: 'center',
          border: isDragActive
            ? '1px dashed var(--mui-palette-primary-main)'
            : '1px dashed var(--mui-palette-divider)',
          borderRadius: 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          outline: 'none',
          p: 6,
          ...(isDragActive && {
            bgcolor: 'var(--mui-palette-action-selected)',
            opacity: 0.5,
          }),
          ...(disabled && {
            opacity: 0.6,
            bgcolor: 'var(--mui-palette-action-disabledBackground)',
          }),
          '&:hover': {
            ...(!isDragActive &&
              !disabled && {
                bgcolor: 'var(--mui-palette-action-hover)',
              }),
          },
        }}
        {...getRootProps()}
      >
        <input {...inputProps} />
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Avatar
            sx={{
              '--Avatar-size': '64px',
              '--Icon-fontSize': 'var(--icon-fontSize-lg)',
              bgcolor: 'var(--mui-palette-background-paper)',
              boxShadow: 'var(--mui-shadows-8)',
              color: 'var(--mui-palette-text-primary)',
            }}
          >
            <Icon icon={icon({ name: 'cloud-arrow-up', style: 'regular' })} />
          </Avatar>
          <Stack spacing={1}>
            <Typography variant="h6">
              <Typography
                component="span"
                sx={{ textDecoration: 'underline' }}
                variant="inherit"
              >
                {t('clickToUpload')}
              </Typography>{' '}
              {t('orDragAndDrop')}
            </Typography>
            {caption ? (
              <Typography color="text.secondary" variant="body2">
                {caption}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
